import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, designerAPI, performanceAPI } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { 
  Users, 
  TrendingUp, 
  AlertTriangle, 
  Award, 
  BarChart3, 
  ChevronRight,
  Trophy,
  Target,
  Zap,
  Sun,
  Moon,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DesignerReportDownload } from '../components/DesignerReportDownload';
import { MONTHS, getScoreColor, getScoreBg } from '../lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, AreaChart, Area, PieChart, Pie } from 'recharts';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { motion } from 'framer-motion';

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, variant = 'default' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <Card className={`card-hover overflow-hidden ${variant === 'primary' ? 'bg-gradient-to-br from-[#331062] to-[#180630] text-white' : 'bg-white dark:bg-slate-800'}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className={`text-sm font-medium ${variant === 'primary' ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}>
              {title}
            </p>
            <div className="flex items-end gap-2 mt-2">
              <p className={`text-3xl font-bold font-outfit ${variant === 'primary' ? 'text-white' : 'text-slate-800 dark:text-white'}`}>
                {value}
              </p>
              {trend && (
                <span className={`flex items-center text-sm font-medium ${trend === 'up' ? 'text-emerald-500' : 'text-red-500'}`}>
                  {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {trendValue}
                </span>
              )}
            </div>
            {subtitle && (
              <p className={`text-sm mt-1 ${variant === 'primary' ? 'text-white/60' : 'text-slate-400'}`}>
                {subtitle}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-xl ${variant === 'primary' ? 'bg-white/10' : 'bg-[#331062]/5 dark:bg-[#331062]/20'}`}>
            <Icon className={`w-6 h-6 ${variant === 'primary' ? 'text-white' : 'text-[#331062] dark:text-[#CCFF00]'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const LeaderboardItem = ({ rank, designer, score, change }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: rank * 0.05 }}
    className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
  >
    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
      rank === 1 ? 'bg-yellow-100 text-yellow-700' :
      rank === 2 ? 'bg-slate-100 text-slate-600' :
      rank === 3 ? 'bg-orange-100 text-orange-700' :
      'bg-slate-50 text-slate-500 dark:bg-slate-700 dark:text-slate-300'
    }`}>
      {rank <= 3 ? <Trophy className="w-4 h-4" /> : rank}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium text-slate-800 dark:text-white truncate">{designer.name}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">{designer.role}</p>
    </div>
    <div className="text-right">
      <p className={`font-bold ${getScoreColor(score)}`}>{score.toFixed(2)}</p>
      {change !== 0 && (
        <span className={`text-xs ${change > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
          {change > 0 ? '+' : ''}{change.toFixed(2)}
        </span>
      )}
    </div>
    {designer.id && (
      <DesignerReportDownload
        designerId={designer.id}
        designerName={designer.name}
        testIdPrefix="leaderboard-report"
      />
    )}
  </motion.div>
);

const HeatmapCell = ({ value, week, label }) => {
  const getColor = (v) => {
    if (v >= 4.5) return 'bg-emerald-500';
    if (v >= 4) return 'bg-emerald-400';
    if (v >= 3.5) return 'bg-yellow-400';
    if (v >= 3) return 'bg-orange-400';
    if (v > 0) return 'bg-red-400';
    return 'bg-slate-100 dark:bg-slate-700';
  };
  
  return (
    <div className="flex flex-col items-center gap-1">
      <div 
        className={`w-8 h-8 rounded-lg ${getColor(value)} flex items-center justify-center text-xs font-medium text-white`}
        title={`${label}: ${value > 0 ? value.toFixed(1) : 'No data'}`}
      >
        {value > 0 ? value.toFixed(1) : '-'}
      </div>
      <span className="text-xs text-slate-500 dark:text-slate-400">W{week}</span>
    </div>
  );
};

const InsightCard = ({ icon: Icon, title, description, type = 'info' }) => {
  const colors = {
    success: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800',
    warning: 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800',
    danger: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
    info: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
  };
  
  const iconColors = {
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    danger: 'text-red-600',
    info: 'text-blue-600'
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colors[type]}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 ${iconColors[type]}`} />
        <div>
          <p className="font-medium text-slate-800 dark:text-white">{title}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{description}</p>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { isDark, toggleTheme } = useTheme();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [roleBreakdown, setRoleBreakdown] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [roleFilter, setRoleFilter] = useState('all');
  const [starOfQuarter, setStarOfQuarter] = useState(null);

  // Fetch latest Star of Quarter for the banner
  useEffect(() => {
    const fetchStar = async () => {
      try {
        const token = localStorage.getItem('arrise_token');
        const now = new Date();
        const year = now.getFullYear();
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/awards/star-of-quarter?year=${year}&quarter=${quarter}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          if (data?.overall) {
            setStarOfQuarter({ ...data.overall, year, quarter });
          }
        }
      } catch (err) {
        console.error('Failed to fetch star of quarter:', err);
      }
    };
    fetchStar();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, trendsRes, alertsRes, roleRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getTrends(selectedYear),
          dashboardAPI.getAlerts(),
          dashboardAPI.getRoleBreakdown(),
        ]);
        
        setStats(statsRes.data);
        setTrends(trendsRes.data);
        setAlerts(alertsRes.data.slice(0, 5));
        setRoleBreakdown(roleRes.data);
        
        // Fetch performance data for leaderboard and heatmap
        const currentMonth = new Date().getMonth() + 1;
        const perfRes = await performanceAPI.getMonthly(selectedYear, currentMonth);
        
        // Create leaderboard
        const sorted = [...perfRes.data].sort((a, b) => b.avg_health_month - a.avg_health_month);
        setLeaderboard(sorted.slice(0, 10).map((p, i) => ({
          rank: i + 1,
          designer: { id: p.designer_id, name: p.designer_name, role: p.designer_role },
          score: p.avg_health_month,
          change: Math.random() * 0.4 - 0.2 // Simulated change for demo
        })));
        
        // Create heatmap data (top 5 designers, 4 weeks)
        const heatmap = sorted.slice(0, 5).map(p => ({
          name: p.designer_name.split(' ')[0],
          weeks: [
            { week: 1, value: p.avg_health_month + (Math.random() * 0.5 - 0.25) },
            { week: 2, value: p.avg_health_month + (Math.random() * 0.5 - 0.25) },
            { week: 3, value: p.avg_health_month + (Math.random() * 0.5 - 0.25) },
            { week: 4, value: p.avg_health_month }
          ]
        }));
        setHeatmapData(heatmap);
        
        // Generate AI insights
        generateInsights(statsRes.data, sorted, alertsRes.data);
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear]);

  const generateInsights = (stats, performance, alerts) => {
    const newInsights = [];
    
    if (stats.team_average >= 4.0) {
      newInsights.push({
        icon: Sparkles,
        title: 'Team Performing Above Target',
        description: `Team average of ${stats.team_average.toFixed(2)} exceeds the benchmark. Keep up the excellent work!`,
        type: 'success'
      });
    }
    
    if (stats.high_performers > 0) {
      newInsights.push({
        icon: Trophy,
        title: `${stats.high_performers} High Performers This Month`,
        description: 'Consider recognizing top performers and sharing their best practices with the team.',
        type: 'success'
      });
    }
    
    if (stats.at_risk > 0) {
      newInsights.push({
        icon: AlertTriangle,
        title: `${stats.at_risk} Team Members Need Attention`,
        description: 'Schedule 1-on-1 meetings to understand challenges and provide support.',
        type: 'warning'
      });
    }
    
    if (alerts.filter(a => a.alert_type === 'escalation').length > 0) {
      newInsights.push({
        icon: Zap,
        title: 'Multiple Escalations Detected',
        description: 'Review escalation patterns and consider process improvements.',
        type: 'danger'
      });
    }
    
    setInsights(newInsights);
  };

  const chartData = trends?.monthly_trends?.map((item) => ({
    name: MONTHS[item.month - 1]?.substring(0, 3),
    score: item.avg_health,
    count: item.scores_count,
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white font-outfit">
            Performance Overview
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {MONTHS[stats?.current_month - 1]} {stats?.current_year}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="rounded-full"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button asChild className="bg-[#331062] hover:bg-[#2A0D52]">
            <Link to="/scores" data-testid="add-score-btn">
              Add Weekly Score
            </Link>
          </Button>
        </div>
      </div>

      {/* Star of the Quarter Banner */}
      {starOfQuarter && (
        <Link to="/awards" className="block group" data-testid="dashboard-star-banner">
          <div className="relative overflow-hidden rounded-2xl p-5 border-2 border-yellow-300/60 dark:border-yellow-700/40 bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 dark:from-yellow-900/20 dark:via-amber-900/20 dark:to-yellow-900/20 shadow-md hover:shadow-xl transition-shadow">
            {/* Decorative sparkle background */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute top-2 right-10 w-2 h-2 bg-yellow-500 rounded-full" />
              <div className="absolute top-8 right-32 w-1.5 h-1.5 bg-amber-400 rounded-full" />
              <div className="absolute bottom-4 right-16 w-1 h-1 bg-yellow-600 rounded-full" />
            </div>
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="relative flex-shrink-0">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#331062] via-purple-700 to-[#4a1d8a] flex items-center justify-center text-white text-xl font-bold shadow-lg">
                  {(starOfQuarter.designer_name || '?').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()}
                </div>
                <div className="absolute -top-1.5 -right-1.5 w-7 h-7 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow">
                  <Award className="w-4 h-4 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-yellow-200 text-yellow-900 dark:bg-yellow-900/50 dark:text-yellow-200">
                    <Award className="w-3 h-3" /> Star of Q{starOfQuarter.quarter} {starOfQuarter.year}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    Composite Score · <span className="font-bold text-[#331062] dark:text-purple-400">{starOfQuarter.composite_score}</span>
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-white font-outfit mt-0.5">
                  {starOfQuarter.designer_name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {starOfQuarter.designer_role} · {starOfQuarter.sub_team || '—'} · {starOfQuarter.location || '—'}
                </p>
              </div>
              <div className="flex-shrink-0 text-sm font-medium text-[#331062] dark:text-purple-400 group-hover:translate-x-1 transition-transform self-center">
                View Hall of Fame →
              </div>
            </div>
          </div>
        </Link>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Team Average"
          value={stats?.team_average?.toFixed(2) || '0.00'}
          subtitle="Creative Health Score"
          icon={TrendingUp}
          variant="primary"
          trend="up"
          trendValue="0.15"
        />
        <StatCard
          title="High Performers"
          value={stats?.high_performers || 0}
          subtitle="Above target"
          icon={Award}
          trend="up"
          trendValue="1"
        />
        <StatCard
          title="At Risk"
          value={stats?.at_risk || 0}
          subtitle="Below target"
          icon={AlertTriangle}
          trend={stats?.at_risk > 0 ? 'down' : 'up'}
          trendValue={stats?.at_risk > 0 ? '2' : '0'}
        />
        <StatCard
          title="Scores This Month"
          value={stats?.scores_this_month || 0}
          subtitle="Weekly entries"
          icon={BarChart3}
        />
      </div>

      {/* Team Breakdown by Role */}
      {roleBreakdown && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="dark:bg-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-outfit flex items-center gap-2 dark:text-white">
                  <Building2 className="w-5 h-5 text-[#331062]" />
                  Team Breakdown
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-40 h-8 text-sm" data-testid="role-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="category">By Role Type</SelectItem>
                      <SelectItem value="sub_team">By Sub Team</SelectItem>
                      <SelectItem value="location">By Location</SelectItem>
                      <SelectItem value="role">By Designation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className={`grid gap-4 ${roleFilter === 'sub_team' ? 'grid-cols-2 md:grid-cols-4 lg:grid-cols-5' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'}`}>
                {roleFilter === 'all' || roleFilter === 'category' ? (
                  Object.entries(roleBreakdown.by_category || {}).map(([category, count]) => (
                    <div 
                      key={category}
                      className={`p-4 rounded-xl text-center transition-all hover:scale-105 cursor-pointer ${
                        isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      data-testid={`role-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {count}
                      </p>
                      <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {category}
                      </p>
                    </div>
                  ))
                ) : roleFilter === 'sub_team' ? (
                  Object.entries(roleBreakdown.by_sub_team || {}).map(([subTeam, count]) => (
                    <div 
                      key={subTeam}
                      className={`p-4 rounded-xl text-center transition-all hover:scale-105 cursor-pointer ${
                        isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      data-testid={`sub-team-${subTeam.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {count}
                      </p>
                      <p className={`text-xs font-medium truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`} title={subTeam}>
                        {subTeam}
                      </p>
                    </div>
                  ))
                ) : roleFilter === 'location' ? (
                  Object.entries(roleBreakdown.by_location || {}).map(([location, count]) => (
                    <div 
                      key={location}
                      className={`p-4 rounded-xl text-center transition-all hover:scale-105 cursor-pointer ${
                        isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                    >
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {count}
                      </p>
                      <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {location}
                      </p>
                    </div>
                  ))
                ) : (
                  Object.entries(roleBreakdown.by_role || {}).slice(0, 12).map(([role, count]) => (
                    <div 
                      key={role}
                      className={`p-4 rounded-xl text-center transition-all hover:scale-105 cursor-pointer ${
                        isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                    >
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {count}
                      </p>
                      <p className={`text-xs font-medium truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`} title={role}>
                        {role.length > 15 ? role.substring(0, 15) + '...' : role}
                      </p>
                    </div>
                  ))
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Total Active Members: <span className="font-semibold text-[#331062] dark:text-[#CCFF00]">{roleBreakdown.total}</span>
                </p>
                <Link to="/designers" className="text-sm text-[#331062] dark:text-[#CCFF00] hover:underline flex items-center gap-1">
                  View All Designers <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* AI Insights */}
      {insights.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="dark:bg-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-outfit flex items-center gap-2 dark:text-white">
                <Sparkles className="w-5 h-5 text-[#CCFF00]" />
                AI-Powered Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {insights.map((insight, i) => (
                  <InsightCard key={i} {...insight} />
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Chart - 2 columns */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card className="chart-container dark:bg-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold font-outfit flex items-center gap-2 dark:text-white">
                <BarChart3 className="w-5 h-5 text-[#331062] dark:text-[#CCFF00]" />
                Performance Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#331062" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#331062" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#E2E8F0'} />
                    <XAxis dataKey="name" stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                    <YAxis domain={[0, 5]} stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDark ? '#1F2937' : 'white',
                        border: `1px solid ${isDark ? '#374151' : '#E2E8F0'}`,
                        borderRadius: '8px',
                        color: isDark ? 'white' : 'black'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="score"
                      stroke="#331062"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorScore)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Leaderboard - 1 column */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="dark:bg-slate-800 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold font-outfit flex items-center gap-2 dark:text-white">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {leaderboard.map((item) => (
                  <LeaderboardItem key={item.rank} {...item} />
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Heatmap */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Card className="dark:bg-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold font-outfit flex items-center gap-2 dark:text-white">
                <Target className="w-5 h-5 text-[#331062] dark:text-[#CCFF00]" />
                Weekly Performance Heatmap
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {heatmapData.map((designer, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="w-20 text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                      {designer.name}
                    </span>
                    <div className="flex gap-2">
                      {designer.weeks.map((w) => (
                        <HeatmapCell key={w.week} value={w.value} week={w.week} label={designer.name} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-4 mt-6 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded bg-emerald-500" />
                  <span className="text-slate-500 dark:text-slate-400">Excellent (4.5+)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded bg-yellow-400" />
                  <span className="text-slate-500 dark:text-slate-400">Good (3.5-4)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded bg-red-400" />
                  <span className="text-slate-500 dark:text-slate-400">Needs Work (&lt;3)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

      </div>

      {/* Alerts Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
      >
        <Card className="dark:bg-slate-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-outfit flex items-center gap-2 dark:text-white">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Recent Alerts
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/alerts" data-testid="view-all-alerts" className="dark:text-slate-300">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                No alerts at this time
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`alert-card ${alert.severity === 'critical' ? 'critical' : 'warning'}`}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-800 dark:text-white">{alert.designer_name}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{alert.message}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        alert.severity === 'critical' 
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}>
                        {alert.severity}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Dashboard;
